import React, { useEffect } from 'react'
import { CoreButton, CoreIconButton, CoreTextField } from 'src/components/core'
import {
  CircularProgress,
  TextField,
  withStyles,
  Typography,
  Popover,
  Tooltip,
} from '@material-ui/core'
import { Icon } from '@iconify/react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import ThemeSettingsDrawer from 'src/components/theme-settings'
import SettingsDrawer from 'src/components/settings-drawer'
import AnalyticsDrawer from 'src/components/analytics-drawer'
import styles from './styles'
import { PatchContent, PatchData } from 'src/model/patch'
import { coursePatchAction } from 'src/reducers/course/action'
import SharePopover from 'src/components/share-popover'
import CoursePreview from 'src/components/preview'
import { track } from '@amplitude/analytics-browser'
import CreditCountBadge from '../credit-count-badge'
import { isMobile } from 'react-device-detect'
import { useSnackbar } from 'notistack'
import PublishChangesPopover from 'src/components/publish-changes-popover'
import { useCoursePublish } from 'src/contexts/course-publish-context'
const CourseToolBar = props => {
  const { classes } = props
  const history = useHistory()
  const dispatch = useDispatch()
  const course = useSelector(state => state.course.course)
  const [title, setTitle] = React.useState(course?.name)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [publishAnchorEl, setPublishAnchorEl] = React.useState(null)
  // Clicking Share on a draft asks before publishing: the user came for a link, and publishing is
  // more than they asked for.
  const [publishPromptAnchorEl, setPublishPromptAnchorEl] = React.useState(null)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const publishState = useCoursePublish()
  const handleOpenModal = () => {
    track('Course_Preview_Clicked', {
      courseId: course.id,
      courseName: course.name,
    })
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  useEffect(() => {
    if (course) {
      setTitle(course.name)
    }
  }, [course])

  // Back goes to the course detail page the editor was opened from.
  const redirectToCollection = () => {
    history.push(course ? `/mini-course/${course.id}/sections` : '/courses')
  }
  const handleTitleChange = event => {
    setTitle(event.target.value)
  }

  const onBlurTitle = value => {
    if (course?.name !== value) {
      const patchSource = new PatchData(null, course.id)
      patchSource.addContent(
        new PatchContent(value, 'name', PatchData.OPERATION_REPLACE),
      )
      dispatch(coursePatchAction(patchSource, () => {}))
    }
  }

  const saving = useSelector(state => state.card.editorLoading)
  const handleClickShare = event => {
    // Unknown is not the same as draft. Until publish state loads, Share behaves as it always
    // did — otherwise a quick click on a live course offers to publish something already live.
    if (publishState?.isLoaded && !publishState.isPublished) {
      setPublishPromptAnchorEl(event.currentTarget)
      return
    }

    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  /**
   * Moving the course back to draft from inside the Share panel invalidates what that panel is
   * showing: it is still offering a link that has just stopped working. Close it. Reopening Share
   * is what asks about publishing again — the user just chose draft, so we do not immediately
   * offer to undo that.
   */
  useEffect(() => {
    if (!publishState?.isLoaded || publishState.isPublished || !anchorEl) {
      return
    }

    setAnchorEl(null)
  }, [publishState?.isLoaded, publishState?.isPublished, anchorEl])

  const handlePublishAndShare = async () => {
    const anchor = publishPromptAnchorEl

    try {
      await publishState.publishCourse()
      track('course_published')
      setPublishPromptAnchorEl(null)
      // Straight into sharing: publishing was the means, the link was the goal.
      setAnchorEl(anchor)
    } catch (error) {
      enqueueSnackbar('Could not publish this course.', {
        variant: 'error',
      })
    }
  }

  const handlePublishChanges = async cardIds => {
    try {
      await publishState.publishChanges(cardIds)
      track('course_changes_published')
      enqueueSnackbar(
        `${cardIds.length} ${
          cardIds.length === 1 ? 'change' : 'changes'
        } published. Learners now see the latest version.`,
        { variant: 'success' },
      )
    } catch (error) {
      enqueueSnackbar('Could not publish these changes.', { variant: 'error' })
    }
  }
  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined
  const [hoveredCourseNaming, setHoveredCourseNaming] = React.useState(false)
  const editorErrorState = useSelector(state => state.card.editorError)
  return (
    <>
      <div className={classes.toolbarFlexStyle}>
        <Tooltip title="Back to course" placement="bottom">
          <CoreIconButton
            size="small"
            color="secondary"
            variant="text"
            onClick={redirectToCollection}
          >
            <Icon icon="ic:round-chevron-left" />
          </CoreIconButton>
        </Tooltip>

        {course && !isMobile && (
          <div
            onMouseEnter={() => {
              setHoveredCourseNaming(true)
            }}
            onMouseLeave={() => {
              setHoveredCourseNaming(false)
            }}
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 4,
              alignItems: 'center',
              width: '100%',
            }}
          >
            <CoreTextField
              className={classes.textfield}
              InputProps={{
                className: classes.textfieldInput,
              }}
              inputProps={{
                style: { padding: 8, backgroundColor: 'transparent' },
              }}
              onBlur={e => onBlurTitle(e.target.value)}
              onChange={e => handleTitleChange(e)}
              value={title}
              color="primary"
              marginTop={false}
            />
            <Icon
              icon="fluent:edit-16-regular"
              fontSize={16}
              color="#002E47"
              style={{ display: hoveredCourseNaming && 'none' }}
            />
            <Icon
              icon="fluent:edit-24-filled"
              fontSize={16}
              color="#002E47"
              style={{ display: hoveredCourseNaming ? 'block' : 'none' }}
            />
            {publishState?.isLoaded && !publishState.isPublished && (
              <Tooltip title="Not visible to learners yet" placement="bottom">
                <Typography component="span" className={classes.draftBadge}>
                  Draft
                </Typography>
              </Tooltip>
            )}
          </div>
        )}
        {course && isMobile && (
          <Typography
            fontSize="14px"
            fontWeight={400}
            color="#002E47"
            fontFamily={'Inter'}
            style={{
              maxWidth: '180px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {course.name}
          </Typography>
        )}
      </div>
      <div className={classes.toolbarFlexStyle}>
        <div style={{ flexGrow: 1 }}></div>
        {!isMobile && <CreditCountBadge dispatch={dispatch} />}
        {!isMobile &&
          publishState?.isPublished &&
          publishState.pendingPages.length > 0 && (
            <CoreButton
              size="small"
              color="secondary"
              variant="text"
              onClick={event => setPublishAnchorEl(event.currentTarget)}
            >
              <span className={classes.publishChangesLabel}>
                Publish changes
                <span className={classes.pendingDot} />
              </span>
            </CoreButton>
          )}

        {!isMobile && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 4,
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              height: '36px',
            }}
          >
            {saving ? (
              <Tooltip title="Changes are being saved...">
                <CircularProgress size={18} color="primary" />
              </Tooltip>
            ) : (
              <Tooltip title="All changes saved">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 4,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {editorErrorState ? (
                    <Icon
                      icon="iconamoon:cloud-error-light"
                      fontSize={18}
                      color="ED4245"
                    />
                  ) : (
                    <Icon
                      icon="ion:cloud-done-outline"
                      color="29D25D"
                      fontSize={18}
                    />
                  )}

                  <Typography
                    fontSize="13px"
                    fontWeight={400}
                    color="#002E47"
                    fontFamily={'Inter'}
                    style={{
                      marginLeft: '4px',
                    }}
                  >
                    {editorErrorState ? 'Unsaved' : 'Saved'}
                  </Typography>
                </div>
              </Tooltip>
            )}
          </div>
        )}
        {/* Theme, settings and analytics live on the course detail page. */}
        <CoreButton
          size="small"
          color="primary"
          variant="text"
          onClick={handleOpenModal}
        >
          Preview
        </CoreButton>
        <CoreButton
          size="small"
          color="primary"
          variant="contained"
          onClick={handleClickShare}
        >
          Share
        </CoreButton>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            style: {
              padding: '16px',
              borderRadius: '16px',
              width: '400px',
            },
          }}
        >
          <SharePopover />
        </Popover>
        <Popover
          open={Boolean(publishPromptAnchorEl)}
          anchorEl={publishPromptAnchorEl}
          onClose={() => setPublishPromptAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            style: {
              padding: '16px',
              borderRadius: '16px',
              width: '360px',
            },
          }}
        >
          <Typography className={classes.publishPromptTitle}>
            Publish this course?
          </Typography>
          <Typography className={classes.publishPromptBody}>
            &quot;{course?.name}&quot; is still a draft, so it has no shareable
            link yet. Publishing makes it visible to learners.
          </Typography>
          <div className={classes.publishPromptActions}>
            <CoreButton
              size="small"
              color="secondary"
              variant="text"
              onClick={() => setPublishPromptAnchorEl(null)}
            >
              Cancel
            </CoreButton>
            <CoreButton
              size="small"
              color="primary"
              variant="contained"
              onClick={handlePublishAndShare}
            >
              Publish and share
            </CoreButton>
          </div>
        </Popover>
        <PublishChangesPopover
          anchorEl={publishAnchorEl}
          onClose={() => setPublishAnchorEl(null)}
          pendingPages={publishState?.pendingPages ?? []}
          onPublish={handlePublishChanges}
        />
        <ThemeSettingsDrawer />
        <SettingsDrawer />
        <AnalyticsDrawer />
        <CoursePreview
          handleCloseModal={handleCloseModal}
          isModalOpen={isModalOpen}
        />
      </div>
    </>
  )
}

export default withStyles(styles)(CourseToolBar)
